<script setup lang="ts">
import {
    nextTick, ref, onUpdated, onUnmounted, watch, computed,
} from 'vue';
import { useDisplay } from 'vuetify';
import { useDialogStore } from '@/stores/dialogStore';
import { useTranslation } from '@/composables';
import { BBtn, BDialogCloseBtn } from '@/design-system';

const props = withDefaults(
    defineProps<{
        id?: string;
        title?: string;
        confirmBtnText?: string;
        cancelBtnText?: string;
        saveAndCloseBtnText?: string;
        showSaveAndClose?: boolean;
        maxWidth?: number | string;
        minHeight?: number | string;
        maxHeight?: number | string;
        height?: number | string;
        titleClasses?: string;
        isLoading?: boolean;
        hideTitle?: boolean;
        hideActions?: boolean;
        class?: string;
        textClasses?: string;
    }>(),
    {
        id: 'default',
        title: '',
        cancelBtnText: 'common.cancel',
        confirmBtnText: 'common.save',
        saveAndCloseBtnText: 'common.saveAndClose',
        showSaveAndClose: true,
        maxWidth: 500,
        minHeight: 'auto',
        titleClasses: '',
        isLoading: false,
        hideActions: false,
    },
);

const emit = defineEmits<{
    confirm: [];
    cancel: [];
    saveAndClose: [];
}>();

const { t } = useTranslation();
const store = useDialogStore();
const { smAndUp } = useDisplay();
let observer: ResizeObserver | null = null;
let mutationObserver: MutationObserver | null = null;
const dialogContentHeight = ref<string>('100%');

const dialogContentId = props.id + '-dialog';

const confirm = (): void => {
    emit('confirm');
};

const saveAndClose = (): void => {
    emit('saveAndClose');
};

const cancel = (): void => {
    emit('cancel');
    store.close(props.id);
};

const calculateDialogContentHeight = async (): Promise<void> => {
    await nextTick();
    const el = document.getElementById(dialogContentId);
    if (el) {
        dialogContentHeight.value = el.scrollHeight + 'px';
    }

    await new Promise(resolve => setTimeout(resolve, 10));
    window.dispatchEvent(new Event('resize'));
};

const dialogOpen = computed(() => store.modals[props.id]);

const computedTitleClasses = computed(() => {
    return props.titleClasses + (props.title !== '' ? ' pt-6 pb-4 px-6' : '');
});

const computedClasses = computed(() => props.class || '');

onUpdated(async () => {
    if (observer) {
        observer.disconnect();
        observer = null;
    }

    if (mutationObserver) {
        mutationObserver.disconnect();
        mutationObserver = null;
    }

    if (!props.maxHeight && !props.height) {
        await calculateDialogContentHeight();

        observer = new ResizeObserver(async () => {
            await calculateDialogContentHeight();
        });

        mutationObserver = new MutationObserver(async () => {
            await calculateDialogContentHeight();
        });

        const el = document.getElementById(dialogContentId);

        if (el) {
            observer.observe(el);
            mutationObserver.observe(el, { childList: true, subtree: true });
        }
    }
});

onUnmounted(() => {
    if (observer) {
        observer.disconnect();
        observer = null;
    }

    if (mutationObserver) {
        mutationObserver.disconnect();
        mutationObserver = null;
    }
});

watch(
    () => props.id,
    newValue => {
        store.initializeModal(newValue);
    },
    { immediate: true },
);

defineExpose({ recalculateHeight: calculateDialogContentHeight });
</script>

<template>
    <VDialog
        v-model="dialogOpen"
        :close-on-back="false"
        :max-width="maxWidth"
        :min-height="minHeight"
        :max-height="maxHeight"
        :height="height"
        :fullscreen="!smAndUp"
        scrollable
        :retain-focus="false"
        :class="computedClasses"
        @keydown.esc="cancel"
    >
        <BDialogCloseBtn @click="cancel" />

        <VCard
            elevation="0"
            :rounded="smAndUp"
        >
            <VCardTitle
                v-if="!hideTitle"
                :style="title === '' ? 'min-height: 1rem' : ''"
                :class="computedTitleClasses"
            >
                <slot name="header">
                    <span>{{ title === '' ? title : t(title) }}</span>
                </slot>
            </VCardTitle>

            <VCardText
                :id="dialogContentId"
                :class="textClasses"
                :style="{ maxHeight: dialogContentHeight }"
            >
                <slot name="body" />
            </VCardText>

            <VSpacer v-show="!smAndUp" />

            <VCardActions
                class="pa-6 justify-end"
                v-if="!hideActions"
            >
                <slot
                    name="actions"
                    v-bind="{ cancel, confirm, saveAndClose }"
                >
                    <slot name="actions-prepend" />

                    <BBtn
                        color="secondary"
                        variant="tonal"
                        rounded
                        class="btn-dialog-cta-action mr-auto"
                        @click="cancel"
                    >
                        {{ t(cancelBtnText) }}
                    </BBtn>

                    <BBtn
                        :variant="showSaveAndClose ? 'outlined' : 'flat'"
                        color="primary"
                        rounded
                        class="btn-dialog-cta-action"
                        :disabled="isLoading"
                        @click="confirm"
                    >
                        {{ t(confirmBtnText) }}
                    </BBtn>

                    <BBtn
                        v-if="showSaveAndClose"
                        variant="flat"
                        color="primary"
                        rounded
                        class="btn-dialog-cta-action"
                        :disabled="isLoading"
                        @click="saveAndClose"
                    >
                        {{ t(saveAndCloseBtnText) }}
                    </BBtn>

                    <slot name="actions-append" />
                </slot>
            </VCardActions>
        </VCard>
    </VDialog>
</template>

<style scoped lang="scss">
:deep(.v-card-text) {
    transition: max-height 0.3s ease;
}
</style>
